import { Order_Read } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import { FetchReason } from '~constants/enums';
import { Order } from '~hooks/useOrders/models';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import {
  subscribeWithMultipleHandlers,
  unsubscribeHandler,
} from '~utils/rtu/rtu-multi-handler';

const SMART_REFRESH_HANDLER_KEY = 'dispatch-smart-refresh';
// Spec: 5-second minimum interval between auto-applies to avoid API hammering.
const AUTO_APPLY_DEBOUNCE_WINDOW_IN_MS = 5000;
// Coalesce consecutive RTUs for the same order into a single apply to avoid jumpy UI.
const SAME_ORDER_RTU_SETTLE_WINDOW_IN_MS = 750;
// Spec: cap queue at 100 entries and switch to "many updates" copy.
const MAX_QUEUED_UPDATES = 100;

type ApplySource = 'auto' | 'cta';

type OrdersFetchOutcome = {
  reason: FetchReason;
  success: boolean;
};

interface UseDispatchOrdersRtuSmartRefreshParams {
  companyId: string | null;
  hasActiveFilters: boolean;
  isAllDatesSelected: boolean;
  isFeatureEnabled: boolean;
  isSafeToAutoApply: boolean;
  onAfterApplySuccess: () => void;
  onRequestRefresh: (
    reason: FetchReason,
    options?: { silent?: boolean },
  ) => Promise<boolean>;
  recentlyMutatedOrderIdsRef: MutableRefObject<Set<string>>;
}

interface UseDispatchOrdersRtuSmartRefreshResult {
  applyQueuedUpdates: () => void;
  dismissBanner: () => void;
  handleOrdersFetchCompleted: (outcome: OrdersFetchOutcome) => void;
  isApplying: boolean;
  isBannerVisible: boolean;
  isQueueCapped: boolean;
  updatesCount: number;
}

export default function useDispatchOrdersRtuSmartRefresh({
  companyId,
  hasActiveFilters,
  isAllDatesSelected,
  isFeatureEnabled,
  isSafeToAutoApply,
  onAfterApplySuccess,
  onRequestRefresh,
  recentlyMutatedOrderIdsRef,
}: UseDispatchOrdersRtuSmartRefreshParams): UseDispatchOrdersRtuSmartRefreshResult {
  const { ordersDispatchStore, toasterStore } = useStores();
  const [queuedOrderIds, setQueuedOrderIds] = useState<string[]>([]);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const [isQueueCapped, setIsQueueCapped] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [hasFailedApply, setHasFailedApply] = useState(false);

  const queuedOrderIdsRef = useRef<string[]>([]);
  const isSafeToAutoApplyRef = useRef(isSafeToAutoApply);
  const isApplyingRef = useRef(isApplying);
  // Mirrors hasFailedApply for timer callbacks; written synchronously alongside
  // setHasFailedApply so a timer firing before React commits cannot read a stale value.
  const hasFailedApplyRef = useRef(false);
  const lastAutoApplyTimestampRef = useRef<number>(0);
  const pendingAutoApplyTimeoutRef = useRef<number | null>(null);
  const pendingSameOrderIdRef = useRef<string | null>(null);
  const pendingSameOrderSettleTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    queuedOrderIdsRef.current = queuedOrderIds;
  }, [queuedOrderIds]);

  useEffect(() => {
    isSafeToAutoApplyRef.current = isSafeToAutoApply;
  }, [isSafeToAutoApply]);

  useEffect(() => {
    isApplyingRef.current = isApplying;
  }, [isApplying]);

  const clearPendingSameOrderSettle = useCallback(() => {
    if (pendingSameOrderSettleTimeoutRef.current) {
      window.clearTimeout(pendingSameOrderSettleTimeoutRef.current);
      pendingSameOrderSettleTimeoutRef.current = null;
    }

    pendingSameOrderIdRef.current = null;
  }, []);

  const clearQueue = useCallback(() => {
    setQueuedOrderIds([]);
    queuedOrderIdsRef.current = [];
    setIsQueueCapped(false);
    setIsBannerDismissed(false);
    hasFailedApplyRef.current = false;
    setHasFailedApply(false);
    clearPendingSameOrderSettle();
  }, [clearPendingSameOrderSettle]);

  const enqueueOrderId = useCallback((orderId: string) => {
    setQueuedOrderIds((prev) => {
      if (prev.includes(orderId)) {
        queuedOrderIdsRef.current = prev;
        return prev;
      }

      if (prev.length >= MAX_QUEUED_UPDATES) {
        setIsQueueCapped(true);
        setIsBannerDismissed(false);
        queuedOrderIdsRef.current = prev;
        return prev;
      }

      const nextQueue = [...prev, orderId];

      setIsBannerDismissed(false);
      queuedOrderIdsRef.current = nextQueue;
      return nextQueue;
    });
  }, []);

  const applyQueuedUpdatesInternal = useCallback(
    async (source: ApplySource) => {
      if (!isFeatureEnabled) {
        return;
      }

      if (isApplyingRef.current || ordersDispatchStore.isLoadingOrders) {
        return;
      }

      if (pendingAutoApplyTimeoutRef.current) {
        window.clearTimeout(pendingAutoApplyTimeoutRef.current);
        pendingAutoApplyTimeoutRef.current = null;
      }

      isApplyingRef.current = true;
      setIsApplying(true);

      const success = await onRequestRefresh(FetchReason.REFRESH, {
        silent: source === 'auto',
      });

      if (success) {
        onAfterApplySuccess();
        clearQueue();

        if (source === 'auto') {
          lastAutoApplyTimestampRef.current = Date.now();
        }
      } else {
        toasterStore.push(
          alert(t('dispatch.smart_refresh.apply_failed'), AlertTypes.error),
        );
        setIsBannerDismissed(false);
        hasFailedApplyRef.current = true;
        setHasFailedApply(true);
      }

      setIsApplying(false);
      isApplyingRef.current = false;
    },
    [
      clearQueue,
      isFeatureEnabled,
      onAfterApplySuccess,
      onRequestRefresh,
      ordersDispatchStore.isLoadingOrders,
      toasterStore,
    ],
  );

  const scheduleAutoApply = useCallback(() => {
    if (!isSafeToAutoApplyRef.current || isApplyingRef.current) {
      return;
    }

    // A failed apply is surfaced through the banner as a manual retry; the
    // auto-apply path must never silently retry it behind the user's back.
    if (hasFailedApplyRef.current) {
      return;
    }

    if (queuedOrderIdsRef.current.length === 0) {
      return;
    }

    const now = Date.now();
    const timeSinceLastAutoApply = now - lastAutoApplyTimestampRef.current;

    if (timeSinceLastAutoApply >= AUTO_APPLY_DEBOUNCE_WINDOW_IN_MS) {
      void applyQueuedUpdatesInternal('auto');
      return;
    }

    if (pendingAutoApplyTimeoutRef.current) {
      return;
    }

    const remainingDelay = AUTO_APPLY_DEBOUNCE_WINDOW_IN_MS - timeSinceLastAutoApply;

    pendingAutoApplyTimeoutRef.current = window.setTimeout(() => {
      pendingAutoApplyTimeoutRef.current = null;

      const isAutoApplyBlocked =
        !isSafeToAutoApplyRef.current ||
        isApplyingRef.current ||
        hasFailedApplyRef.current;

      if (isAutoApplyBlocked) {
        return;
      }

      if (queuedOrderIdsRef.current.length === 0) {
        return;
      }

      void applyQueuedUpdatesInternal('auto');
    }, remainingDelay);
  }, [applyQueuedUpdatesInternal]);

  const scheduleSameOrderAutoApply = useCallback(
    (orderId: string) => {
      pendingSameOrderIdRef.current = orderId;

      if (pendingSameOrderSettleTimeoutRef.current) {
        window.clearTimeout(pendingSameOrderSettleTimeoutRef.current);
      }

      pendingSameOrderSettleTimeoutRef.current = window.setTimeout(() => {
        pendingSameOrderSettleTimeoutRef.current = null;
        pendingSameOrderIdRef.current = null;
        scheduleAutoApply();
      }, SAME_ORDER_RTU_SETTLE_WINDOW_IN_MS);
    },
    [scheduleAutoApply],
  );

  const handleRtuReceived = useCallback(
    ({ data }: { data: Order_Read }) => {
      if (!isFeatureEnabled) {
        return;
      }

      let parsedOrder: Order;

      try {
        parsedOrder = Order.parse(data);
      } catch (error) {
        console.error('[Dispatch Smart Refresh] Failed to parse order RTU', error);
        return;
      }

      const isOrderInList = ordersDispatchStore.orders.some(
        (order) => order.id === parsedOrder.id,
      );

      if (isOrderInList && !ordersDispatchStore.preventUpsertOrder) {
        ordersDispatchStore.upsertOrder(parsedOrder);
        return;
      }

      if (recentlyMutatedOrderIdsRef.current.has(parsedOrder.id)) {
        return;
      }

      const shouldQueueUpdate =
        ordersDispatchStore.isLoadingOrders ||
        !isOrderInList ||
        hasActiveFilters ||
        isAllDatesSelected ||
        ordersDispatchStore.preventUpsertOrder;

      if (!shouldQueueUpdate) {
        return;
      }

      enqueueOrderId(parsedOrder.id);

      if (isSafeToAutoApplyRef.current) {
        const pendingOrderId = pendingSameOrderIdRef.current;

        if (pendingOrderId && pendingOrderId !== parsedOrder.id) {
          clearPendingSameOrderSettle();
          scheduleAutoApply();
          return;
        }

        scheduleSameOrderAutoApply(parsedOrder.id);
      }
    },
    [
      clearPendingSameOrderSettle,
      enqueueOrderId,
      hasActiveFilters,
      isAllDatesSelected,
      isFeatureEnabled,
      ordersDispatchStore,
      recentlyMutatedOrderIdsRef,
      scheduleAutoApply,
      scheduleSameOrderAutoApply,
    ],
  );

  useEffect(() => {
    if (!companyId || !isFeatureEnabled) {
      return;
    }

    subscribeWithMultipleHandlers<{ data: Order_Read }>(
      realTimeChannels.CompanyOrderUpdateChannel,
      companyId,
      SMART_REFRESH_HANDLER_KEY,
      handleRtuReceived,
    );

    return () => {
      unsubscribeHandler(
        realTimeChannels.CompanyOrderUpdateChannel,
        companyId,
        SMART_REFRESH_HANDLER_KEY,
      );
    };
  }, [companyId, handleRtuReceived, isFeatureEnabled]);

  useEffect(() => {
    if (!isFeatureEnabled) {
      clearQueue();
    }
  }, [clearQueue, isFeatureEnabled]);

  // Updates queued while a work-in-progress guard was active (edit/drag/bulk/dialog)
  // must not stay stranded behind the CTA banner: once the guard clears, apply them
  // silently through the same debounced path as live RTUs. Fire only on the actual
  // unsafe -> safe transition — this effect also re-runs when scheduleAutoApply is
  // recreated (e.g. the store's isLoadingOrders toggling), and firing then would
  // silently retry a failed apply that the banner reserves for a manual retry.
  const previousIsSafeToAutoApplyRef = useRef(isSafeToAutoApply);

  useEffect(() => {
    const guardJustCleared = isSafeToAutoApply && !previousIsSafeToAutoApplyRef.current;
    previousIsSafeToAutoApplyRef.current = isSafeToAutoApply;

    if (guardJustCleared) {
      scheduleAutoApply();
    }
  }, [isSafeToAutoApply, scheduleAutoApply]);

  useEffect(() => {
    return () => {
      if (pendingAutoApplyTimeoutRef.current) {
        window.clearTimeout(pendingAutoApplyTimeoutRef.current);
        pendingAutoApplyTimeoutRef.current = null;
      }

      if (pendingSameOrderSettleTimeoutRef.current) {
        window.clearTimeout(pendingSameOrderSettleTimeoutRef.current);
        pendingSameOrderSettleTimeoutRef.current = null;
      }

      pendingSameOrderIdRef.current = null;
    };
  }, []);

  const handleOrdersFetchCompleted = useCallback(
    ({ reason, success }: OrdersFetchOutcome) => {
      if (queuedOrderIdsRef.current.length === 0) {
        return;
      }

      if (reason === FetchReason.FILTERS_CHANGED) {
        if (success) {
          clearQueue();
          return;
        }

        // The failed fetch strands the queued updates: with no guard active the
        // banner would stay hidden and nothing would re-trigger a silent apply.
        // Treat it like a failed apply so the CTA stays reachable for a retry.
        setIsBannerDismissed(false);
        hasFailedApplyRef.current = true;
        setHasFailedApply(true);
        toasterStore.push(
          alert(t('dispatch.smart_refresh.filter_refresh_failed'), AlertTypes.error),
        );
        return;
      }

      if (reason === FetchReason.REFRESH && success) {
        clearQueue();
      }
    },
    [clearQueue, toasterStore],
  );

  const applyQueuedUpdates = useCallback(() => {
    void applyQueuedUpdatesInternal('cta');
  }, [applyQueuedUpdatesInternal]);

  const dismissBanner = useCallback(() => {
    setIsBannerDismissed(true);
  }, []);

  // The CTA banner is a deferral mechanism, not a notification: it shows only while a
  // work-in-progress guard blocks the silent apply, or after an apply failed and the
  // user needs a manual retry. Idle updates apply silently with no banner at all.
  const isApplyDeferredToUser = !isSafeToAutoApply || hasFailedApply;
  const isBannerVisible =
    isFeatureEnabled &&
    queuedOrderIds.length > 0 &&
    !isBannerDismissed &&
    isApplyDeferredToUser;

  return {
    applyQueuedUpdates,
    dismissBanner,
    handleOrdersFetchCompleted,
    isApplying,
    isBannerVisible,
    isQueueCapped,
    updatesCount: queuedOrderIds.length,
  };
}
